/** Color/style mapping for Background Job health and Production Readiness check status -
 * kept separate from the JSON dictionaries (which hold the human-readable labels) so a
 * status string maps to both a translation key and a consistent visual treatment. */
export function jobHealthClass(health) {
  switch (health) {
    case "HEALTHY": return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "RUNNING": return "bg-blue-100 text-blue-700 border-blue-200";
    case "WARNING": return "bg-amber-100 text-amber-700 border-amber-200";
    case "FAILED": return "bg-red-100 text-red-700 border-red-200";
    default: return "bg-gray-100 text-gray-600 border-gray-200";
  }
}

export function readinessStatusClass(status) {
  switch (status) {
    case "READY": return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "MOCK": return "bg-blue-100 text-blue-700 border-blue-200";
    case "DISABLED_INTENTIONALLY": return "bg-slate-100 text-slate-600 border-slate-200";
    case "NOT_APPLICABLE": return "bg-slate-100 text-slate-500 border-slate-200";
    case "MISSING_CONFIGURATION": return "bg-amber-100 text-amber-700 border-amber-200";
    case "ERROR": return "bg-red-100 text-red-700 border-red-200";
    case "REQUIRES_PLATFORM_CONFIRMATION": return "bg-indigo-100 text-indigo-700 border-indigo-200";
    default: return "bg-gray-100 text-gray-600 border-gray-200";
  }
}

export function overallReadinessClass(status) {
  switch (status) {
    case "READY": return "bg-emerald-50 border-emerald-300 text-emerald-800";
    case "READY_WITH_REQUIRED_CONFIGURATION": return "bg-amber-50 border-amber-300 text-amber-800";
    case "NOT_READY": return "bg-red-50 border-red-300 text-red-800";
    default: return "bg-gray-50 border-gray-300 text-gray-800";
  }
}

/** Unifies the two very different shapes the backend returns (`scheduled_jobs`, each with
 * real run history, vs `not_scheduled_jobs`, static honesty placeholders with no run history)
 * into one row shape the table/summary can treat uniformly. */
export function normalizeJobRow(job, isScheduled) {
  if (isScheduled) {
    return {
      key: job.name, label: job.label, module: job.module, displayStatus: job.health,
      enabled: true, mode: job.live ? "LIVE" : "MOCK", schedule: job.schedule,
      lastRun: job.last_run, runsToday: job.runs_today, nextRun: job.next_run_estimate,
      manualTriggerAllowed: job.manual_trigger_allowed, purpose: job.purpose, raw: job,
    };
  }
  const displayStatus = job.status === "MOCK" ? "MOCK" : "DISABLED";
  return {
    key: job.name, label: job.label, module: job.module, displayStatus,
    enabled: false, mode: job.status === "MOCK" ? "MOCK" : "N/A", schedule: job.status,
    lastRun: null, runsToday: null, nextRun: null,
    manualTriggerAllowed: false, purpose: job.note, raw: job,
  };
}

/** Both current reminder jobs happen to share a `reminders_sent` stat key even though the
 * rest of their stats shape differs - that's the only cross-job count safe to generalize
 * into a "Succeeded" column without fabricating a metric neither job actually tracks. */
export function deriveRunCounts(run) {
  if (!run) return { processed: "—", succeeded: "—", failed: "—" };
  const stats = run.stats || {};
  const scannedKey = Object.keys(stats).find((k) => k.endsWith("_scanned"));
  const processed = scannedKey !== undefined ? stats[scannedKey] : "—";
  const succeeded = stats.reminders_sent !== undefined ? stats.reminders_sent : "—";
  const failed = run.status === "SUCCESS" ? 0 : "—";
  return { processed, succeeded, failed };
}
